import { enqueueSnackbar } from "notistack";
import { SubmitHandler, UseControllerProps, useForm } from "react-hook-form";
import { useScope } from "~/hooks/useScope";
import { getError } from "~/lib/handle-error";
import {
  GetMetricsDocument,
  GetSecurityModelDocument,
  ScoreRating,
  SlaStartDateConfig,
  useGetSecurityModelQuery,
  useUpdateSecurityModelMutation,
} from "~/operations";
import { Metrics } from "~/lib/arrow";
import { SlaFormInput, SlaRatings } from "../types";
import { useEffect } from "react";

export function UseSlaConfiguration() {
  const { activeScope } = useScope();

  const { data } = useGetSecurityModelQuery({
    variables: {
      scopeMrn: activeScope?.mrn || "",
    },
    skip: !activeScope,
  });

  const [updateSecurityModel, { loading }] = useUpdateSecurityModelMutation({
    refetchQueries: [
      {
        query: GetSecurityModelDocument,
        variables: {
          scopeMrn: activeScope?.mrn || "",
        },
      },
      // Dashboard metrics don't seem to be reloading
      {
        query: GetMetricsDocument,
        variables: {
          entityMrn: activeScope?.mrn || "",
          metricMrns: [Metrics.MTTRSla],
        },
      },
    ],
  });

  const form = useForm<SlaFormInput>({
    mode: "onBlur",
    defaultValues,
  });

  const formRules: UseControllerProps<SlaFormInput>["rules"] = {
    min: 0,
    required: true,
  };

  useEffect(() => {
    const slas = data?.securityModel?.slas;
    if (!slas?.findings) return;

    const returnedValues: SlaFormInput =
      slas.findings
        ?.filter((sla) => sla.rating !== ScoreRating.None)
        .reduce((acc, sla) => {
          acc[sla.rating as SlaRatings] = {
            daysToResolve: sla.daysToResolve,
            daysBeforeWarning: sla.daysBeforeWarning,
          };
          return acc;
        }, defaultValues) || defaultValues;

    returnedValues.startDateConfig =
      slas.startDateConfig ?? SlaStartDateConfig.CveDetected;

    form.reset(returnedValues);
  }, [data?.securityModel?.slas]);

  const onSubmit: SubmitHandler<SlaFormInput> = async (data) => {
    if (!activeScope) return;
    if (!form.formState.isDirty || !form.formState.isValid) return;

    try {
      const { startDateConfig, ...slaData } = data;

      await updateSecurityModel({
        variables: {
          input: {
            scopeMrn: activeScope.mrn,
            slas: {
              findings: Object.entries(slaData).map(([key, value]) => ({
                rating: key as SlaRatings,
                daysToResolve: value.daysToResolve,
                daysBeforeWarning: value.daysBeforeWarning,
              })),
              startDateConfig,
            },
          },
        },
      });
      enqueueSnackbar("SLA configuration updated successfully", {
        variant: "success",
      });
    } catch (error) {
      const message = getError(error);
      enqueueSnackbar(`Failed to update SLA configuration: ${message}`, {
        variant: "error",
      });
    }
  };

  return {
    form,
    formRules,
    updateSecurityModel,
    onSubmit,
    slaFindings: data?.securityModel?.slas?.findings,
    loading,
  };
}

const defaultValues: SlaFormInput = {
  [ScoreRating.Low]: {
    daysToResolve: 0,
    daysBeforeWarning: 0,
  },
  [ScoreRating.Medium]: {
    daysToResolve: 0,
    daysBeforeWarning: 0,
  },
  [ScoreRating.High]: {
    daysToResolve: 0,
    daysBeforeWarning: 0,
  },
  [ScoreRating.Critical]: {
    daysToResolve: 0,
    daysBeforeWarning: 0,
  },
  startDateConfig: SlaStartDateConfig.CveDetected,
} satisfies SlaFormInput;
